import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorMessages } from '@app/shared/constants';
import {
  CreateSsoConnectionPayload,
  SsoConnection,
  SsoConnectionDetail,
  SsoConnectionListItem,
  UpdateSsoConnectionPayload,
} from '@app/shared/models/sso-connection.model';

const BASE_URL = `${environment.APP_SERVICES_URL}/admin/sso/connections`;

/**
 * Wraps the five admin SSO connection endpoints. Reads need `view.sso_connections`; writes need
 * `modify.sso_connections`. The client secret is write-only and is never part of a response.
 */
@Injectable({ providedIn: 'root' })
export class SsoConnectionService {
  constructor(private http: HttpClient) {}

  public list(): Observable<SsoConnectionListItem[]> {
    return this.http
      .get<SsoConnectionListItem[]>(BASE_URL)
      .pipe(catchError((error) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  public get(uuid: string): Observable<SsoConnectionDetail> {
    return this.http
      .get<SsoConnectionDetail>(`${BASE_URL}/${uuid}`)
      .pipe(catchError((error) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  public create(payload: CreateSsoConnectionPayload): Observable<SsoConnection> {
    return this.http
      .post<SsoConnection>(BASE_URL, payload)
      .pipe(catchError((error) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  public update(uuid: string, payload: UpdateSsoConnectionPayload): Observable<SsoConnection> {
    return this.http
      .put<SsoConnection>(`${BASE_URL}/${uuid}`, payload)
      .pipe(catchError((error) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  /** Soft delete. The API answers 409 while the connection is still assigned to a company. */
  public remove(uuid: string): Observable<void> {
    return this.http
      .delete<void>(`${BASE_URL}/${uuid}`)
      .pipe(catchError((error) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }
}
